import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { useEffect } from 'react'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { type InsuranceCarrier } from './insuranceCarrier'
import { type InsuranceEstimate } from './insuranceEstimate'
import { type DealValueSource, type LeadTemperature } from './myPipeline'
import { type SalesLeadTag } from './salesLeadTag'
import { type SequenceStatus } from './sequence'
import { type Recommendation } from './workQueue'

export enum SalesLeadStatus {
  COLD = 'COLD',
  CALLED = 'CALLED',
  BAD_CONTACT_INFO = 'BAD_CONTACT_INFO',
  ACTIVE = 'ACTIVE',
  APPLICATION_VIEWED = 'APPLICATION_VIEWED',
  APPLICATION_COMPLETED = 'APPLICATION_COMPLETED',
  QUOTING = 'QUOTING',
  QUOTE_SENT = 'QUOTE_SENT',
  CLOSED_WON = 'CLOSED_WON',
  CLOSED_LOST = 'CLOSED_LOST',
  UNQUALIFIED = 'UNQUALIFIED',
  NOT_INTERESTED = 'NOT_INTERESTED',
}

export const salesLeadStatusLabels: Record<SalesLeadStatus, string> = {
  [SalesLeadStatus.COLD]: 'Untouched',
  [SalesLeadStatus.CALLED]: 'Called',
  [SalesLeadStatus.BAD_CONTACT_INFO]: 'Bad Contact Info',
  [SalesLeadStatus.ACTIVE]: 'Engaged',
  [SalesLeadStatus.APPLICATION_VIEWED]: 'Application Viewed',
  [SalesLeadStatus.APPLICATION_COMPLETED]: 'Application Completed',
  [SalesLeadStatus.QUOTING]: 'Quoting',
  [SalesLeadStatus.QUOTE_SENT]: 'Quote Sent',
  [SalesLeadStatus.CLOSED_WON]: 'Closed/Won',
  [SalesLeadStatus.CLOSED_LOST]: 'Closed/Lost',
  [SalesLeadStatus.UNQUALIFIED]: 'Closed/Unquotable',
  [SalesLeadStatus.NOT_INTERESTED]: 'Not Interested',
}

export const salesLeadStatusColors: Record<SalesLeadStatus, string> = {
  [SalesLeadStatus.COLD]: 'bg-slate-100 text-slate-700',
  [SalesLeadStatus.CALLED]: 'bg-zinc-100 text-zinc-700',
  [SalesLeadStatus.BAD_CONTACT_INFO]: 'bg-yellow-100 text-yellow-800',
  [SalesLeadStatus.ACTIVE]: 'bg-blue-100 text-blue-700',
  [SalesLeadStatus.APPLICATION_VIEWED]: 'bg-indigo-100 text-indigo-700',
  [SalesLeadStatus.APPLICATION_COMPLETED]: 'bg-violet-100 text-violet-700',
  [SalesLeadStatus.QUOTING]: 'bg-amber-100 text-amber-700',
  [SalesLeadStatus.QUOTE_SENT]: 'bg-cyan-100 text-cyan-700',
  [SalesLeadStatus.CLOSED_WON]: 'bg-emerald-100 text-emerald-700',
  [SalesLeadStatus.CLOSED_LOST]: 'bg-red-100 text-red-700',
  [SalesLeadStatus.UNQUALIFIED]: 'bg-orange-100 text-orange-700',
  [SalesLeadStatus.NOT_INTERESTED]: 'bg-rose-100 text-rose-700',
}

/** Why a rep closed a lead as lost; required to move a lead to Closed/Lost. */
export enum ClosedLostReason {
  BAD_CONTACT_INFO = 'BAD_CONTACT_INFO',
  GHOSTED = 'GHOSTED',
  PREFERRED_ANOTHER_BROKER = 'PREFERRED_ANOTHER_BROKER',
  STAYED_WITH_INCUMBENT = 'STAYED_WITH_INCUMBENT',
  PRICE = 'PRICE',
  COVERAGE_GAPS = 'COVERAGE_GAPS',
  SPEED = 'SPEED',
  DECLINED_OR_UNQUOTABLE = 'DECLINED_OR_UNQUOTABLE',
  UNKNOWN = 'UNKNOWN',
  OTHER = 'OTHER',
}

export const closedLostReasonLabels: Record<ClosedLostReason, string> = {
  [ClosedLostReason.BAD_CONTACT_INFO]: 'Bad contact info',
  [ClosedLostReason.GHOSTED]: 'Ghosted',
  [ClosedLostReason.PREFERRED_ANOTHER_BROKER]: 'Preferred another broker',
  [ClosedLostReason.STAYED_WITH_INCUMBENT]: 'Stayed with incumbent',
  [ClosedLostReason.PRICE]: 'Price',
  [ClosedLostReason.COVERAGE_GAPS]: 'Coverage gaps',
  [ClosedLostReason.SPEED]: 'Speed (we moved too slow)',
  [ClosedLostReason.DECLINED_OR_UNQUOTABLE]: 'Declinations / unquotable',
  [ClosedLostReason.UNKNOWN]: 'Unknown',
  [ClosedLostReason.OTHER]: 'Other (see notes)',
}

/** The reason and note a move to Closed/Lost carries. */
export interface ClosedLostDetails {
  closedLostReason: ClosedLostReason
  notes: string
}

// Canonical pipeline order (matches the backend STAGE_ORDER) — drives the
// Kanban column order and any ordered stage rendering.
export const salesLeadStageOrder: SalesLeadStatus[] = [
  SalesLeadStatus.COLD,
  SalesLeadStatus.CALLED,
  SalesLeadStatus.BAD_CONTACT_INFO,
  SalesLeadStatus.ACTIVE,
  SalesLeadStatus.APPLICATION_VIEWED,
  SalesLeadStatus.APPLICATION_COMPLETED,
  SalesLeadStatus.QUOTING,
  SalesLeadStatus.QUOTE_SENT,
  SalesLeadStatus.CLOSED_WON,
  SalesLeadStatus.CLOSED_LOST,
  SalesLeadStatus.UNQUALIFIED,
  SalesLeadStatus.NOT_INTERESTED,
]

export enum SalesLeadActivityType {
  CALL = 'CALL',
  CALL_INBOUND = 'CALL_INBOUND',
  CALL_INBOUND_MISSED = 'CALL_INBOUND_MISSED',
  CALL_OUTBOUND = 'CALL_OUTBOUND',
  CALL_OUTBOUND_MISSED = 'CALL_OUTBOUND_MISSED',
  EMAIL = 'EMAIL',
  EMAIL_INBOUND = 'EMAIL_INBOUND',
  EMAIL_OUTBOUND = 'EMAIL_OUTBOUND',
  EMAIL_OUTBOUND_DRAFT = 'EMAIL_OUTBOUND_DRAFT',
  SMS = 'SMS',
  SMS_INBOUND = 'SMS_INBOUND',
  SMS_OUTBOUND = 'SMS_OUTBOUND',
  SMS_OUTBOUND_DRAFT = 'SMS_OUTBOUND_DRAFT',
  NOTE = 'NOTE',
  STATUS_CHANGE = 'STATUS_CHANGE',
  FIELD_UPDATE = 'FIELD_UPDATE',
  AI_SUMMARY = 'AI_SUMMARY',
  CARRIER_ELIGIBLE = 'CARRIER_ELIGIBLE',
  CARRIER_INELIGIBLE = 'CARRIER_INELIGIBLE',
  // Customer timeline events: their notes are Markdown.
  WEB_DOT_ENTRY = 'WEB_DOT_ENTRY',
  SIGNED_UP = 'SIGNED_UP',
  SIGNED_IN = 'SIGNED_IN',
  APP_OPENED = 'APP_OPENED',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  USER_INVITED = 'USER_INVITED',
  USER_JOINED = 'USER_JOINED',
  ACCOUNT_LINKED = 'ACCOUNT_LINKED',
  DUPLICATE_BLOCKED = 'DUPLICATE_BLOCKED',
  ORGS_MERGED = 'ORGS_MERGED',
  FUNNEL_NEEDS_REVIEW = 'FUNNEL_NEEDS_REVIEW',
  APPLICATION_STARTED = 'APPLICATION_STARTED',
  APPLICATION_COMPLETED = 'APPLICATION_COMPLETED',
  APPLICATION_SUBMITTED = 'APPLICATION_SUBMITTED',
  SENT_TO_CARRIERS = 'SENT_TO_CARRIERS',
  REQUOTE_REQUESTED = 'REQUOTE_REQUESTED',
  REQUOTED = 'REQUOTED',
  INDICATION_RECEIVED = 'INDICATION_RECEIVED',
  INDICATION_FAILED = 'INDICATION_FAILED',
  NO_CARRIER_QUOTED = 'NO_CARRIER_QUOTED',
  QUOTES_SENT = 'QUOTES_SENT',
  QUOTES_COMPARED = 'QUOTES_COMPARED',
  QUOTE_SHORTLISTED = 'QUOTE_SHORTLISTED',
  INDICATIONS_SELECTED = 'INDICATIONS_SELECTED',
  QUOTE_READY = 'QUOTE_READY',
  QUOTE_SELECTED = 'QUOTE_SELECTED',
  POLICY_BOUND = 'POLICY_BOUND',
  POLICY_CANCELED = 'POLICY_CANCELED',
  CARRIER_UPDATE = 'CARRIER_UPDATE',
  CARRIER_ISSUE = 'CARRIER_ISSUE',
  DOCUMENT_SIGNED = 'DOCUMENT_SIGNED',
  LOSS_RUN_UPDATE = 'LOSS_RUN_UPDATE',
  LARGE_FLEET_PACKET = 'LARGE_FLEET_PACKET',
  MVR_COMPLETED = 'MVR_COMPLETED',
  ELD_CONNECTED = 'ELD_CONNECTED',
  BROKER_NOTE = 'BROKER_NOTE',
  CLAIM_REPORTED = 'CLAIM_REPORTED',
  CLAIM_SUBMITTED = 'CLAIM_SUBMITTED',
  ENDORSEMENT_SUBMITTED = 'ENDORSEMENT_SUBMITTED',
  ENDORSEMENT_UPDATED = 'ENDORSEMENT_UPDATED',
  ASKED_CAI = 'ASKED_CAI',
}

export interface LeadContact {
  id: string
  name: string
  isPrimary: boolean
  primaryEmail: string
  secondaryEmails: string[]
  primaryPhone: string
  secondaryPhones: string[]
}

export interface EditableSalesLeadFields {
  dotNumber: string | null
  legalName: string
  dbaName: string
  addressLine1: string
  city: string
  state: string
  zipCode: string
  currentInsurer: string
  policyExpirationDate: string | null
  dealValue: string | null
  dealValueSource: DealValueSource
  nextAction: string
  expectedCloseMonth: string | null
  temperature: LeadTemperature | ''
}

export interface SalesLeadActivityAttachment {
  id: string
  fileName: string
  fileExtension: string
  fileSize: number
  fileUrl: string | null
}

export type CallMediaStatus = 'none' | 'pending' | 'stored' | 'failed'

export interface SalesLeadActivity {
  id: string
  activityType: SalesLeadActivityType
  carrier?: string | null
  notes: string
  emailSubject?: string | null
  emailFrom?: string | null
  emailTo?: string | null
  emailOpened?: boolean
  emailHasLinks?: boolean
  emailClicked?: boolean
  draftSubject?: string | null
  draftBody?: string | null
  draftSmsTo?: string | null
  /** The lead's files an AI email draft attaches. */
  draftAttachments?: SalesLeadActivityAttachment[]
  oldStatus: string
  newStatus: string
  createdByName: string
  createdByMe: boolean
  /** Which author icon to show: 'phonic' | 'system' | 'user'. */
  createdByKind?: string
  /** The author's headshot, else their legacy avatar — for the activity icon. */
  createdByAvatar?: string
  attachments: SalesLeadActivityAttachment[]
  callDirection?: 'inbound' | 'outbound' | null
  callDurationSeconds?: number | null
  recordingStatus?: CallMediaStatus | null
  transcriptStatus?: CallMediaStatus | null
  smsBody?: string | null
  smsFrom?: string | null
  smsTo?: string | null
  smsStatus?: 'queued' | 'sent' | 'delivered' | 'failed' | 'received' | null
  /** Driver name when the SMS came from a converted customer's driver. */
  smsSenderDriver?: string | null
  detached?: boolean
  /** Set on carrier-directed activities (shown in the lead's Carriers tab). */
  insuranceCarrier?: string | null
  insuranceCarrierContact?: string | null
  carrierCounterparty?: string | null
  /** The lead↔carrier address this email rode, when it came through a channel. */
  emailChannel?: string | null
  /** The customer's browser session when it happened, for its replay. */
  posthogSessionId?: string
  createdAt: string
}

/** The per-lead forwarding address for one assigned carrier. */
export interface CarrierChannel {
  carrier: string
  idShort: string
  email: string
}

export interface SalesLeadListItem {
  id: string
  dotNumber: string
  legalName: string
  dbaName: string
  state: string
  status: SalesLeadStatus
  assignedRep: string | null
  assignedRepName: string
  /** The rep's headshot (else their legacy avatar); '' when they have neither. */
  assignedRepAvatar: string
  currentInsurer: string
  policyExpirationDate: string | null
  daysUntilExpiration: number | null
  daysInCurrentStage: number
  powerUnits: number
  oosStatus: boolean
  commonAuthStatus: string
  lastActivityAt: string | null
  updatedAt: string
  isFollowing: boolean
  hasUnread: boolean
  /** Came in through the public DOT funnel rather than our outbound motion. */
  isInbound: boolean
  // Status of the lead's live sequence (RUNNING/PAUSED), else null.
  sequenceStatus: SequenceStatus | null
  conversionScore: number | null
  scoreUpdatedAt: string | null
}

export interface FollowUpReminder {
  id: string
  reminderDate: string // UTC ISO8601
  message: string
}

export interface CarrierAppetiteRow {
  kind: 'indication' | 'quote'
  vendor: string
  outcome: string
  date: string
}

export enum CabReportStatus {
  PENDING = 'pending',
  READY = 'ready',
  FAILED = 'failed',
}

export interface CabReport {
  status: CabReportStatus
  fileUrl: string | null
  documentId: string | null
  dateCreated: string | null
}

export enum SafetyRating {
  GOOD = 'GOOD',
  MODERATE = 'MODERATE',
  POOR = 'POOR',
}

export const safetyRatingLabels: Record<SafetyRating, string> = {
  [SafetyRating.GOOD]: 'Good',
  [SafetyRating.MODERATE]: 'Moderate',
  [SafetyRating.POOR]: 'Poor',
}

export enum BuyerType {
  RENEWAL = 'RENEWAL',
  ACTIVATION = 'ACTIVATION',
}

export const buyerTypeLabels: Record<BuyerType, string> = {
  [BuyerType.RENEWAL]: 'Renewal',
  [BuyerType.ACTIVATION]: 'Activation',
}

/** The first rule that matched when the lead's buyer type was decided. */
export enum BuyerTypeRule {
  CURRENT_INSURER = 'CURRENT_INSURER',
  AUTHORITY_ACTIVE = 'AUTHORITY_ACTIVE',
  INTRASTATE = 'INTRASTATE',
  CONFIRMED_UNINSURED = 'CONFIRMED_UNINSURED',
  DEFAULT = 'DEFAULT',
}

export enum AuthorityStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  NONE = 'NONE',
  UNKNOWN = 'UNKNOWN',
}

export const authorityStatusLabels: Record<AuthorityStatus, string> = {
  [AuthorityStatus.ACTIVE]: 'Active',
  [AuthorityStatus.INACTIVE]: 'Inactive',
  [AuthorityStatus.PENDING]: 'Pending',
  [AuthorityStatus.NONE]: 'None',
  [AuthorityStatus.UNKNOWN]: 'Unknown',
}

export enum AuthoritySource {
  FMCSA = 'FMCSA',
  CAB = 'CAB',
}

export interface SafetyScore {
  rating: SafetyRating
  headline: string
  summary: string
  generatedBy: string
  generatedAt: string
}

export enum EligibilityCarrier {
  GEICO = 'GEICO',
  COVERWHALE = 'COVERWHALE',
}

export const eligibilityCarrierLabels: Record<EligibilityCarrier, string> = {
  [EligibilityCarrier.GEICO]: 'GEICO',
  [EligibilityCarrier.COVERWHALE]: 'Cover Whale',
}

const CARRIER_LOGO_BASE = 'https://static.cai.insure/carriers'

export const eligibilityCarrierIcons: Record<EligibilityCarrier, string> = {
  [EligibilityCarrier.GEICO]: `${CARRIER_LOGO_BASE}/geico-icon.png`,
  [EligibilityCarrier.COVERWHALE]: `${CARRIER_LOGO_BASE}/coverwhale-icon.png`,
}

export enum EligibilityStatus {
  ELIGIBLE = 'eligible',
  NOT_ELIGIBLE = 'not_eligible',
  NOT_CHECKED = 'not_checked',
}

export const eligibilityStatusLabels: Record<EligibilityStatus, string> = {
  [EligibilityStatus.ELIGIBLE]: 'Eligible',
  [EligibilityStatus.NOT_ELIGIBLE]: 'Not eligible',
  [EligibilityStatus.NOT_CHECKED]: 'Not checked',
}

/** One carrier's latest eligibility verdict on a lead, or "not checked". */
export interface CarrierEligibility {
  vendor: EligibilityCarrier
  vendorName: string
  iconUrl: string
  status: EligibilityStatus
  /** Decline reasons, or the warnings on an eligible verdict. */
  notes: string
  checkedAt: string | null
  checkedByName: string
  /** What the lead lacks before this check can run, e.g. "Needs a ZIP code". */
  missingForCheck: string
}

/** One pick in the leads list's eligibility filter, sent as `<carrier>:<status>`. */
export interface EligibilityFilterOption {
  value: string
  carrier: EligibilityCarrier
  eligible: boolean
}

export const eligibilityFilterOptions: EligibilityFilterOption[] = Object.values(EligibilityCarrier).flatMap(
  (carrier) => [
    { value: `${carrier}:${EligibilityStatus.ELIGIBLE}`, carrier, eligible: true },
    { value: `${carrier}:${EligibilityStatus.NOT_ELIGIBLE}`, carrier, eligible: false },
  ]
)

export interface SalesLead extends SalesLeadListItem {
  /** Set only while the lead is Closed/Lost. */
  closedLostReason: ClosedLostReason | ''
  addressLine1: string
  city: string
  zipCode: string
  drivers: number
  mcs150Date: string | null
  commonAuthStatus: string
  safetyRating: string
  oosStatus: boolean
  interstate: boolean
  hazmat: boolean
  cargoCarried: string[]
  cabLastFetched: string | null
  convertedOrg: string | null
  createdAt: string
  activities: SalesLeadActivity[]
  carrierActivities: SalesLeadActivity[]
  availableTransitions: SalesLeadStatus[]
  lastReadAt: string | null
  followUpReminder: FollowUpReminder | null
  scoreFactors: Record<string, unknown> | null
  scoreReasoning: string
  buyerType: BuyerType
  /** '' until the lead is first classified. */
  buyerTypeRule: BuyerTypeRule | ''
  /** FMCSA has the carrier as intrastate only, so it makes no federal filing. */
  intrastateOnly: boolean
  /** When the buyer type last got an FMCSA answer; null until it has one. */
  fmcsaCheckedAt: string | null
  /** The authority the buyer type was decided on: FMCSA's while fresh, else CAB's. Null until first classified. */
  authorityStatus: AuthorityStatus | null
  authoritySource: AuthoritySource | null
  /** FMCSA's interstate answer; null until it has one. */
  fmcsaInterstate: boolean | null
  recommendation: Recommendation | null
  safetyScore: SafetyScore | null
  carrierAppetite: CarrierAppetiteRow[]
  carrierEligibility: CarrierEligibility[]
  cabReport: CabReport | null
  tags: SalesLeadTag[]
  contacts: LeadContact[]
  assignedCarriers: InsuranceCarrier[]
  carrierChannels: CarrierChannel[]
}

export interface SalesLeadStats {
  total: number
  byStatus: Record<SalesLeadStatus, number>
  expiring30Days: number
  unassigned: number
}

export enum LeadSource {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export interface SalesLeadFilters {
  page?: number
  source?: LeadSource
  // Comma-separated list of SalesLeadStatus values (the list view filters by
  // multiple statuses); the pipeline board passes a single status per column.
  status?: string
  assignedRep?: string
  expiringWithinDays?: number
  // Excludes leads whose policy expires within this many days; combined with
  // `expiringWithinDays` it forms an "expires between X and Y days" window.
  notExpiringWithinDays?: number
  state?: string
  search?: string
  ordering?: string
  unread?: boolean
  followUp?: boolean
  minScore?: number
  safetyRating?: string
  buyerType?: BuyerType
  minUnits?: number
  maxUnits?: number
  // Comma-separated list of SalesLeadTag ids; matches leads having ANY of them.
  tags?: string
  // Comma-separated list of SalesLeadTag ids; excludes leads having ANY of them.
  excludeTags?: string
  // Comma-separated `<carrier>:<eligible|not_eligible>` picks; matches leads holding ANY of them.
  eligibility?: string
}

export function buildSalesLeadParams(params?: SalesLeadFilters): Record<string, string> {
  const queryParams: Record<string, string> = {}
  if (params?.page && params.page > 1) queryParams.page = String(params.page)
  if (params?.status) queryParams.status = params.status
  if (params?.source) queryParams.source = params.source
  if (params?.assignedRep) queryParams.assigned_rep = params.assignedRep
  if (params?.expiringWithinDays) queryParams.expiring_within_days = String(params.expiringWithinDays)
  if (params?.notExpiringWithinDays)
    queryParams.not_expiring_within_days = String(params.notExpiringWithinDays)
  if (params?.state) queryParams.state = params.state
  if (params?.search) queryParams.search = params.search
  if (params?.ordering) queryParams.ordering = params.ordering
  if (params?.unread) queryParams.unread = '1'
  if (params?.followUp) queryParams.follow_up = '1'
  if (params?.minScore) queryParams.min_score = String(params.minScore)
  if (params?.safetyRating) queryParams.safety_rating = params.safetyRating
  if (params?.buyerType) queryParams.buyer_type = params.buyerType
  if (params?.minUnits) queryParams.min_units = String(params.minUnits)
  if (params?.maxUnits) queryParams.max_units = String(params.maxUnits)
  if (params?.tags) queryParams.tags = params.tags
  if (params?.excludeTags) queryParams.exclude_tags = params.excludeTags
  if (params?.eligibility) queryParams.eligibility = params.eligibility
  return queryParams
}

function getSalesLeads(params?: SalesLeadFilters): Promise<PaginatedResults<SalesLeadListItem>> {
  return axios
    .get('/api/v1/insure/internal/sales-leads/', { params: buildSalesLeadParams(params) })
    .then((response) => toCamelCase(response.data) as PaginatedResults<SalesLeadListItem>)
}

export function useSalesLeads(params?: SalesLeadFilters) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.SALES_LEADS, params],
    queryFn: () => getSalesLeads(params),
  })
  return {
    leads: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isLoading,
  }
}

// Per-column infinite scroll for the pipeline board: pass a single `status`
// plus the shared filters; pages are 1-indexed and follow `next` until null.
export function useInfiniteSalesLeads(params: SalesLeadFilters) {
  return useInfiniteQuery({
    queryKey: [CacheKeys.SALES_LEADS, 'infinite', params],
    queryFn: ({ pageParam }) => getSalesLeads({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
  })
}

export function getSalesLead(id: string): Promise<SalesLead> {
  return axios
    .get(`/api/v1/insure/internal/sales-leads/${id}/`)
    .then((response) => toCamelCase(response.data) as SalesLead)
}

export function useSalesLead(id: string) {
  return useQuery({
    queryKey: [CacheKeys.SALES_LEADS, id],
    queryFn: () => getSalesLead(id),
    enabled: !!id,
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
  })
}

/** A customer's timeline when it has no sales lead to read it from. */
export function useCustomerActivities(orgId: string) {
  return useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, orgId, 'activities'],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/organisations/${orgId}/activities/`)
        .then((response) => toCamelCase(response.data) as SalesLeadActivity[]),
  })
}

export function useAddCustomerNote(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notes: string) =>
      axios
        .post(`/api/v1/insure/organisations/${orgId}/notes/`, { notes })
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'activities'] })
    },
  })
}

// Filters are honored server-side (shared get_queryset). Pass everything EXCEPT
// `status` on the board so per-column counts reflect the active filter set.
export function getSalesLeadStats(
  filters?: Omit<SalesLeadFilters, 'status' | 'page' | 'ordering'>
): Promise<SalesLeadStats> {
  return axios
    .get('/api/v1/insure/internal/sales-leads/stats/', { params: buildSalesLeadParams(filters) })
    .then((response) => toCamelCase(response.data) as SalesLeadStats)
}

export function useSalesLeadStats(filters?: Omit<SalesLeadFilters, 'status' | 'page' | 'ordering'>) {
  return useQuery({
    queryKey: [CacheKeys.SALES_LEADS, 'stats', filters],
    queryFn: () => getSalesLeadStats(filters),
  })
}

export function useUpdateSalesLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<EditableSalesLeadFields>) =>
      axios
        .patch(`/api/v1/insure/internal/sales-leads/${id}/`, toSnakeCase(patch))
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: (updated) => {
      // Write the updated lead into the detail cache synchronously so consumers
      // (header card, SMS/call buttons) see the new value immediately — not only
      // after the async refetch/2s poll. Without this, hitting SMS right after
      // editing a phone could text the stale number.
      queryClient.setQueryData([CacheKeys.SALES_LEADS, updated.id], updated)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_PIPELINE] })
    },
  })
}

export function useTransitionLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      targetStatus,
      notes,
      closedLostReason,
    }: {
      id: string
      targetStatus: SalesLeadStatus
    } & Partial<ClosedLostDetails>) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${id}/transition/`,
          toSnakeCase({ targetStatus, notes, closedLostReason })
        )
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_PIPELINE] })
    },
  })
}

// Optimistic cross-column move for the pipeline board: instantly relocates the
// card between the two per-column infinite caches, rolls back on error, and
// reconciles (incl. counts) on settle. Keyed identically to useInfiniteSalesLeads.
export function useMovePipelineLead(filters: Omit<SalesLeadFilters, 'status' | 'page' | 'ordering'>) {
  const queryClient = useQueryClient()
  type Infinite = InfiniteData<PaginatedResults<SalesLeadListItem>>
  const infiniteKey = (status: SalesLeadStatus) => [CacheKeys.SALES_LEADS, 'infinite', { ...filters, status }]

  return useMutation({
    mutationFn: ({
      id,
      targetStatus,
      notes,
      closedLostReason,
    }: {
      lead: SalesLeadListItem
      targetStatus: SalesLeadStatus
      id: string
    } & Partial<ClosedLostDetails>) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${id}/transition/`,
          toSnakeCase({ targetStatus, notes, closedLostReason })
        )
        .then((response) => toCamelCase(response.data) as SalesLead),
    onMutate: async ({ lead, targetStatus }) => {
      const fromKey = infiniteKey(lead.status)
      const toKey = infiniteKey(targetStatus)
      await queryClient.cancelQueries({ queryKey: fromKey })
      await queryClient.cancelQueries({ queryKey: toKey })
      const prevFrom = queryClient.getQueryData<Infinite>(fromKey)
      const prevTo = queryClient.getQueryData<Infinite>(toKey)
      if (prevFrom) {
        queryClient.setQueryData<Infinite>(fromKey, {
          ...prevFrom,
          pages: prevFrom.pages.map((page) => ({
            ...page,
            results: page.results.filter((l) => l.id !== lead.id),
          })),
        })
      }
      if (prevTo && prevTo.pages.length > 0) {
        const [first, ...rest] = prevTo.pages
        queryClient.setQueryData<Infinite>(toKey, {
          ...prevTo,
          pages: [{ ...first, results: [{ ...lead, status: targetStatus }, ...first.results] }, ...rest],
        })
      }
      return { fromKey, toKey, prevFrom, prevTo }
    },
    onError: (_error, _vars, context) => {
      if (context?.prevFrom) queryClient.setQueryData(context.fromKey, context.prevFrom)
      if (context?.prevTo) queryClient.setQueryData(context.toKey, context.prevTo)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useAddActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      activityType,
      notes,
    }: {
      id: string
      activityType: SalesLeadActivityType
      notes: string
    }) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/add_activity/`, toSnakeCase({ activityType, notes }))
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useSendSalesLeadEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      subject,
      content,
      htmlContent,
      to,
      inReplyToActivityId,
      attachmentDocumentIds,
      templateId,
      templateBaseline,
    }: {
      id: string
      subject: string
      content: string
      htmlContent: string
      to?: string[] | null
      inReplyToActivityId?: string
      attachmentDocumentIds?: string[]
      templateId?: string | null
      templateBaseline?: string
    }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${id}/send-email/`,
          toSnakeCase({
            subject,
            content,
            htmlContent,
            to,
            inReplyToActivityId,
            attachmentDocumentIds,
            templateId,
            templateBaseline,
          })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useSendSalesLeadSms() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      content,
      to,
      inReplyToActivityId,
    }: {
      id: string
      content: string
      to?: string | null
      inReplyToActivityId?: string
    }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${id}/send-sms/`,
          toSnakeCase({ content, to, inReplyToActivityId })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useAssignLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, repId }: { id: string; repId: string | null }) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/assign/`, { rep_id: repId })
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDeleteLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`/api/v1/insure/internal/sales-leads/${id}/`).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export interface ConvertLeadResponse {
  orgId: string
  userId: string
}

export function useConvertLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/convert-to-customer/`)
        .then((response) => toCamelCase(response.data) as ConvertLeadResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useFollowLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/follow/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUnfollowLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/unfollow/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useSetFollowUpReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reminderDate, note }: { id: string; reminderDate: string; note: string }) =>
      axios
        .put(
          `/api/v1/insure/internal/sales-leads/${id}/follow-up-reminder/`,
          toSnakeCase({ reminderDate, note, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
        )
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_REMINDERS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_PIPELINE] })
    },
  })
}

export function useClearFollowUpReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .delete(`/api/v1/insure/internal/sales-leads/${id}/follow-up-reminder/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_REMINDERS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_PIPELINE] })
    },
  })
}

export function useMarkLeadRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => axios.post(`/api/v1/insure/internal/sales-leads/${id}/mark-read/`),
    onSuccess: (_data, id) => {
      // Optimistically clear hasUnread on THIS lead's detail row instead
      // of invalidating it. An invalidate causes an immediate refetch, and
      // if a new activity has streamed in between mark-read POST and the
      // refetch, hasUnread comes back true → useMarkLeadReadWhenUnread
      // fires mark-read again → tight loop until the rate limit
      // melts the UI down.
      queryClient.setQueryData<SalesLead | undefined>([CacheKeys.SALES_LEADS, id], (old) =>
        old ? { ...old, hasUnread: false } : old
      )
      // Refresh every OTHER SalesLead query (list, stats, other leads'
      // detail) so unread badges/counts update — just not the one we
      // already patched.
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === CacheKeys.SALES_LEADS && q.queryKey[1] !== id,
      })
    },
  })
}

/** Bump the read pointer once each time a followed lead turns unread. A failed bump waits for the next change. */
export function useMarkLeadReadWhenUnread(lead: SalesLead | undefined) {
  const { mutate: markLeadRead } = useMarkLeadRead()
  // Keyed on the flags alone: the lead is a new object every 2s refetch and the mutation
  // result a new one every render, so depending on either would retry a failure.
  useEffect(() => {
    if (lead?.isFollowing && lead.hasUnread) markLeadRead(lead.id)
  }, [lead?.id, lead?.isFollowing, lead?.hasUnread, markLeadRead])
}

export function useRefreshFromCab() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/refresh_from_cab/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

// Reads the carrier's FMCSA registration now. The response is the relabelled lead,
// written straight into its detail so the check button doesn't flash back.
export function useCheckFmcsa() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/check-fmcsa/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: (lead, id) => {
      queryClient.setQueryData([CacheKeys.SALES_LEADS, id], lead)
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === CacheKeys.SALES_LEADS && q.queryKey[1] !== id,
      })
    },
  })
}

// Kicks off the background CAB report fetch. The lead detail query polls, so the
// sidebar button flips to "ready" on its own once the task stores the document.
export function useFetchCabReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/fetch-cab-report/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

// Generates the safety score inline (server reads the stored CAB report PDF + LLM),
// returning the updated lead so the merged card refreshes immediately.
export function useGenerateSafetyScore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/generate-safety-score/`)
        .then((response) => toCamelCase(response.data) as SalesLead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

interface GeicoEligibilityResult {
  eligible: boolean
  ineligibilityReason: string | null
  /** GEICO already insures this USDOT, so it won't quote it — not an appetite decline. */
  existingPolicyholder: boolean
  status: string
  errorMessage: string | null
}

// The tag picker renders the tag list and saves from lead.tags; a verdict can change both.
function useRefreshAfterVerdict() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEAD_TAGS] })
  }
}

export function useCheckGeicoEligibility() {
  const refreshAfterVerdict = useRefreshAfterVerdict()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/check-geico-eligibility/`)
        .then((response) => toCamelCase(response.data) as GeicoEligibilityResult),
    onSuccess: refreshAfterVerdict,
  })
}

interface CoverwhalePrescreenResult {
  eligible: boolean | null
  declineReasons: string[]
  warnings: string[]
  status: string
  errorMessage: string | null
}

export function useCheckCoverwhalePrescreen() {
  const refreshAfterVerdict = useRefreshAfterVerdict()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${id}/check-coverwhale-prescreen/`)
        .then((response) => toCamelCase(response.data) as CoverwhalePrescreenResult),
    onSuccess: refreshAfterVerdict,
  })
}

export function useSendDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      activityId,
      subject,
      content,
      htmlContent,
      to,
      attachmentDocumentIds,
      leadAttachmentIds,
    }: {
      leadId: string
      activityId: string
      subject: string
      content: string
      htmlContent?: string
      to?: string[]
      attachmentDocumentIds?: string[]
      /** Files already on the lead, by file id. */
      leadAttachmentIds?: string[]
    }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${leadId}/drafts/${activityId}/send/`,
          toSnakeCase({ subject, content, htmlContent, to, attachmentDocumentIds, leadAttachmentIds })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUpdateDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      activityId,
      content,
      subject,
    }: {
      leadId: string
      activityId: string
      content: string
      subject?: string
    }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${leadId}/drafts/${activityId}/update/`,
          toSnakeCase({ content, subject })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: (updatedActivity, vars) => {
      queryClient.setQueryData<SalesLead>([CacheKeys.SALES_LEADS, vars.leadId], (old) =>
        old
          ? {
              ...old,
              activities: old.activities.map((a) => (a.id === updatedActivity.id ? updatedActivity : a)),
            }
          : old
      )
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUpdateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, activityId, notes }: { leadId: string; activityId: string; notes: string }) =>
      axios
        .patch(
          `/api/v1/insure/internal/sales-leads/${leadId}/activities/${activityId}/`,
          toSnakeCase({ notes })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: (updatedActivity, vars) => {
      queryClient.setQueryData<SalesLead>([CacheKeys.SALES_LEADS, vars.leadId], (old) =>
        old
          ? {
              ...old,
              activities: old.activities.map((a) => (a.id === updatedActivity.id ? updatedActivity : a)),
            }
          : old
      )
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDiscardDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, activityId }: { leadId: string; activityId: string }) =>
      axios.post(`/api/v1/insure/internal/sales-leads/${leadId}/drafts/${activityId}/discard/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export interface InstantlyCampaign {
  id: string
  name: string
  status: number
}

export function useInstantlyCampaigns(search?: string, enabled = true) {
  return useQuery({
    queryKey: [CacheKeys.SALES_LEADS, 'instantly-campaigns', search],
    queryFn: () =>
      axios
        .get('/api/v1/insure/internal/sales-leads/instantly-campaigns/', { params: search ? { search } : {} })
        .then((response) => response.data as InstantlyCampaign[]),
    staleTime: 60_000,
    enabled,
  })
}

export interface AddToCampaignResult {
  leadsUploaded: number
  duplicatedLeads: number
  skippedCount: number
}

export function useAddLeadToInstantlyCampaign() {
  return useMutation({
    mutationFn: ({ leadId, campaignId }: { leadId: string; campaignId: string }) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${leadId}/add-to-instantly-campaign/`, {
          campaign_id: campaignId,
        })
        .then((response) => toCamelCase(response.data) as AddToCampaignResult),
  })
}

export interface IngestFromCabParams {
  states: string[]
  truckMin: number
  truckMax: number
  insExpMin: number
  insExpMax: number
  assignedRepId?: string
  campaignId?: string
  skipAssignment?: boolean
}

export interface IngestFromCabResponse {
  status: string
  taskId: string
}

export interface IngestionStatusResult {
  created: number
  updated: number
  skipped: number
  errors: number
  assigned: number
  campaignAdded: number
}

export function useIngestFromCab() {
  return useMutation({
    mutationFn: (params: IngestFromCabParams) =>
      axios
        .post('/api/v1/insure/internal/sales-leads/ingest-from-cab/', toSnakeCase(params))
        .then((res) => toCamelCase(res.data) as IngestFromCabResponse),
  })
}

export interface IngestFromCsvResponse {
  status: string
  taskId: string
}

export interface CsvIngestionStatusResult {
  error?: string
  created?: number
  updated?: number
  errors?: number
  assigned?: number
  campaignAdded?: number
  rowErrors?: { rowNumber: number; error: string }[]
  headerMapping?: Record<string, string | null>
}

export interface FlaggedCsvLead {
  dotNumber: string
  legalName: string
  status: string
  statusLabel: string
  assignedRepName: string | null
  isBlocklisted: boolean
  flagReasons: string[]
}

export interface CheckCsvLeadsResult {
  dotColumnFound: boolean
  totalInCsv: number
  flaggedLeads: FlaggedCsvLead[]
  error?: string
}

export function useCheckCsvLeads() {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return axios
        .post('/api/v1/insure/internal/sales-leads/check-csv-leads/', formData)
        .then((res) => toCamelCase(res.data) as CheckCsvLeadsResult)
    },
  })
}

export function useIngestFromCsv() {
  return useMutation({
    mutationFn: ({
      file,
      assignedRepId,
      campaignId,
      skipAssignment,
      tagIds,
    }: {
      file: File
      assignedRepId?: string
      campaignId?: string
      skipAssignment?: boolean
      tagIds?: string[]
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (assignedRepId) formData.append('assigned_rep_id', assignedRepId)
      if (campaignId) formData.append('campaign_id', campaignId)
      if (skipAssignment) formData.append('skip_assignment', 'true')
      tagIds?.forEach((tagId) => formData.append('tag_ids', tagId))
      return axios
        .post('/api/v1/insure/internal/sales-leads/ingest-from-csv/', formData)
        .then((res) => toCamelCase(res.data) as IngestFromCsvResponse)
    },
  })
}

export function useCreateNewDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${leadId}/drafts/new/`, {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useCreateNewSmsDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${leadId}/sms-drafts/new/`, {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useSendSmsDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      activityId,
      to,
      content,
    }: {
      leadId: string
      activityId: string
      to: string
      content: string
    }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${leadId}/sms-drafts/${activityId}/send/`,
          toSnakeCase({ to, content })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUpdateSmsDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, activityId, content }: { leadId: string; activityId: string; content: string }) =>
      axios
        .post(
          `/api/v1/insure/internal/sales-leads/${leadId}/sms-drafts/${activityId}/update/`,
          toSnakeCase({ content })
        )
        .then((response) => toCamelCase(response.data) as SalesLeadActivity),
    onSuccess: (updatedActivity, vars) => {
      queryClient.setQueryData<SalesLead>([CacheKeys.SALES_LEADS, vars.leadId], (old) =>
        old
          ? {
              ...old,
              activities: old.activities.map((a) => (a.id === updatedActivity.id ? updatedActivity : a)),
            }
          : old
      )
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDiscardSmsDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, activityId }: { leadId: string; activityId: string }) =>
      axios.post(`/api/v1/insure/internal/sales-leads/${leadId}/sms-drafts/${activityId}/discard/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useCreateContact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<LeadContact>) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${leadId}/contacts/`, toSnakeCase(data))
        .then((res) => toCamelCase(res.data) as SalesLead),
    onSuccess: (lead) => {
      // Write the updated lead into the detail cache so the contact card AND the
      // SMS/Call buttons reflect the change immediately, not only after a poll.
      queryClient.setQueryData([CacheKeys.SALES_LEADS, leadId], lead)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUpdateContact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, ...data }: { contactId: string } & Partial<LeadContact>) =>
      axios
        .patch(`/api/v1/insure/internal/sales-leads/${leadId}/contacts/${contactId}/`, toSnakeCase(data))
        .then((res) => toCamelCase(res.data) as SalesLead),
    onSuccess: (lead) => {
      queryClient.setQueryData([CacheKeys.SALES_LEADS, leadId], lead)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDeleteContact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      axios
        .delete(`/api/v1/insure/internal/sales-leads/${leadId}/contacts/${contactId}/delete/`)
        .then((res) => toCamelCase(res.data) as SalesLead),
    onSuccess: (lead) => {
      queryClient.setQueryData([CacheKeys.SALES_LEADS, leadId], lead)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useSetPrimaryContact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      axios
        .post(`/api/v1/insure/internal/sales-leads/${leadId}/contacts/${contactId}/set-primary/`)
        .then((res) => toCamelCase(res.data) as SalesLead),
    onSuccess: (lead) => {
      queryClient.setQueryData([CacheKeys.SALES_LEADS, leadId], lead)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

/** One anonymous visit to the public DOT funnel, as shown on the lead. */
export interface LeadFunnelVisit {
  id: string
  dotNumber: string
  dateCreated: string
  handedOffAt: string | null
  adoptedOrgId: string | null
}

export interface LeadEstimates {
  estimates: InsuranceEstimate[]
  sessions: LeadFunnelVisit[]
}

export function useLeadEstimates(id: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.SALES_LEADS, id, 'estimates'],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/internal/sales-leads/${id}/estimates/`)
        .then((response) => toCamelCase(response.data)) as Promise<LeadEstimates>,
    enabled: !!id,
  })
}
